import { createReducer } from "@reduxjs/toolkit";
import remove from "lodash/remove";

import {
  BULK_CHANGE_COLLAB_STATE_ROUTINE,
  CHANGE_COLLAB_STATE_ROUTINE,
  FETCH_ARCHIVED_POSTS_ROUTINE,
  FETCH_DRAFT_POSTS_ROUTINE,
  FETCH_UPCOMING_POSTS_ROUTINE,
  FETCH_POSTS_IGNORE,
  HANDLE_STATUS_CHANGED,
  INIT_POSTS_PAGE_ROUTINE,
  REMOVE_POSTS_ROUTINE,
  SEND_POSTS_TO_DRAFT_ROUTINE,
  SEND_POSTS_TO_QUEUE_ROUTINE,
  TOGGLE_POST_CHECKBOX,
  TOGGLE_SELECT_ALL_POSTS,
  UPDATE_POSTS_PAGE_NAME,
} from "constants/ActionTypes";
import { POST_STATES } from "constants/PostsView";

const initialState: any = {
  byId: {},
  allIds: [],
  selectedIds: [],
  edittingIds: [],
  allSelected: false,
  total: null,
  hasMore: false,
  filters: null,
  allLoaded: false, // for inf scroll
  error: null,
};

export const postsReducer = createReducer(initialState, (builder) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  builder.addCase(UPDATE_POSTS_PAGE_NAME, (state, action: any) => {
    const { page } = action.payload as { page: string };
    state.page = page;
    if (!state.common) {
      state.common = {};
    }
    state.common.page = page;

    return state;
  });
  builder.addCase(INIT_POSTS_PAGE_ROUTINE.TRIGGER, () => {
    return initialState;
  });
  // Note: we intentionally do NOT reset entities on FILTER_COLLAB_STATUS,
  // FILTER_POST_TYPE, or RELOAD_POSTS_ROUTINE.
  //
  // Why: resetting state to initialState on TRIGGER causes a visible flash
  // of empty content — the UI renders an empty list/calendar while waiting
  // for the server response. This is redundant because fetchPostsSuccess
  // (called on SUCCESS) already clears byId, allIds, selectedIds, etc.
  // via clearState=true and atomically repopulates from the response.
  //
  // Safety: the reloadPosts saga guards against duplicate fetches with an
  // isFetching check, so there is no race condition. The old TRIGGER reset
  // was actually harmful when isFetching was true — state got cleared but
  // no replacement fetch followed, leaving the UI permanently empty.
  builder.addCase(FETCH_UPCOMING_POSTS_ROUTINE.SUCCESS, (state, action) => {
    const { posts, filters, hasMore } = action.payload as any;
    state.filters = filters;
    state.error = null; // Clear any previous errors

    let newQueuedAccounts: Record<string, boolean> = {};

    if (posts.length) {
      state.filters.lastNextRunAt = posts[posts.length - 1].timestamp;
    }

    for (var i = posts.length - 1, post: any; i >= 0; i--) {
      post = posts[i];
      if (post.queued && !newQueuedAccounts[post.accountId]) {
        newQueuedAccounts[post.accountId] = true;
        state.filters.accountFilters[post.accountId] = {
          lastOrdinal: post.ordinal,
        };
      }
    }

    state.hasMore = hasMore;
    state.allLoaded = !(posts.length > 1);
    return fetchPostsSuccess(state, action);
  });
  builder.addCase(FETCH_UPCOMING_POSTS_ROUTINE.FAILURE, (state, action) => {
    state.error =
      (action as any).message || "Failed to load posts. Please try again.";
    state.hasMore = false;
    state.allLoaded = true;
    return state;
  });
  builder.addCase(FETCH_DRAFT_POSTS_ROUTINE.SUCCESS, (state, action) => {
    state.error = null; // Clear any previous errors
    return fetchPostsSuccess(state, action);
  });
  builder.addCase(FETCH_DRAFT_POSTS_ROUTINE.FAILURE, (state, action) => {
    state.error =
      (action as any).message ||
      "Failed to load draft posts. Please try again.";
    return state;
  });
  builder.addCase(FETCH_ARCHIVED_POSTS_ROUTINE.SUCCESS, (state, action) => {
    state.error = null; // Clear any previous errors
    return fetchPostsSuccess(state, action);
  });
  builder.addCase(FETCH_ARCHIVED_POSTS_ROUTINE.FAILURE, (state, action) => {
    state.error =
      (action as any).message ||
      "Failed to load archived posts. Please try again.";
    return state;
  });
  builder.addCase(FETCH_POSTS_IGNORE, (state, action) => {
    // Handle cases where posts fetch was skipped.
    // - Normal skips (e.g., "No accounts selected") only pass `message`
    // - Error skips (e.g., accounts timeout/failure) pass both `message` and `error`
    // Only surface `error` to the UI, not `message`
    const payload = (action as any).payload as {
      message?: string;
      error?: string;
    };
    if (payload?.error) {
      state.error = payload.error;
    }
    return state;
  });
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  builder.addCase(TOGGLE_SELECT_ALL_POSTS, (state, _action) => {
    state.allSelected = !state.allSelected;

    state.selectedIds = [];
    if (state.allSelected) {
      state.selectedIds = state.allIds;
    }

    return state;
  });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  builder.addCase(TOGGLE_POST_CHECKBOX, (state, action: any) => {
    const { postId } = action.payload as { postId: string };

    if (state.selectedIds.includes(postId)) {
      remove(state.selectedIds, (selectedId: any) => selectedId === postId);
    } else {
      state.selectedIds.push(postId);
    }

    state.allSelected = false;
    if (
      state.allIds.length > 0 &&
      state.selectedIds.length === state.allIds.length
    ) {
      state.allSelected = true;
    }

    return state;
  });
  builder.addCase(CHANGE_COLLAB_STATE_ROUTINE.SUCCESS, (state, action) =>
    updateCollabState(state, action)
  );
  builder.addCase(BULK_CHANGE_COLLAB_STATE_ROUTINE.SUCCESS, (state, action) => {
    state.selectedIds = [];
    state.allSelected = false;
    return updateCollabState(state, action);
  });
  builder.addCase(SEND_POSTS_TO_DRAFT_ROUTINE.SUCCESS, (state, action) =>
    clearSelected(state, action)
  );
  builder.addCase(SEND_POSTS_TO_QUEUE_ROUTINE.SUCCESS, (state, action) =>
    clearSelected(state, action)
  );
  builder.addCase(REMOVE_POSTS_ROUTINE.SUCCESS, (state, action) =>
    clearSelected(state, action)
  );
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  builder.addCase(HANDLE_STATUS_CHANGED, (state, action: any) => {
    const { post, postType } = action.payload as {
      post: any;
      postType: string;
    };
    const postRow = state.byId[post.postId];

    if (!postRow) return state;

    // TODO: Check how this affect on the post status filters
    // if is draft
    if (
      postType.toLowerCase() === POST_STATES.DRAFT.toLowerCase() &&
      postRow.schedule
    ) {
      postRow.schedule.postStatusKey = post.newStatus.key;
    } else {
      postRow.postStatusKey = post.newStatus.key;
    }
    return state;
  });
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const clearSelected = (state: any, _action: any) => {
  state.selectedIds = [];
  state.allSelected = false;
  return state;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const updateCollabState = (state: any, action: any) => {
  const { postIds, postStatusKey } = action.payload;
  postIds.forEach((postId: any) => {
    // in some scenarios we do not have a full state here
    // eg when editing a post in calendar or planner - as that state is angularjs
    if (state.byId && state.byId[postId]) {
      if (state.byId[postId].schedule) {
        state.byId[postId].schedule.postStatusKey = postStatusKey;
      } else {
        state.byId[postId].postStatusKey = postStatusKey;
      }
    }
  });
  return state;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const fetchPostsSuccess = (state: any, action: any, clearState = true) => {
  state.allSelected = false;

  if (clearState) {
    state.byId = {};
    state.allIds = [];
    state.selectedIds = [];
    state.total = null;
    state.filters = null;
    state.allLoaded = false;
  }

  const { posts, total } = action.payload;
  if (!posts) {
    return state;
  }

  posts.forEach((post: any) => {
    state.byId[post._id] = post;
    if (!state.allIds.includes(post._id)) {
      state.allIds.push(post._id);
    }
  });

  if (total) {
    state.total = total;
  }

  return state;
};

export function partitionSelectedPostIds(posts: any[], selectedPostIds: any[]) {
  let postIds: any[] = [];
  let qPostIds: any[] = [];

  posts.forEach((post: any) => {
    if (!selectedPostIds.includes(post._id)) {
      return;
    }
    post.queued ? qPostIds.push(post._id) : postIds.push(post._id);
  });

  return { postIds, qPostIds };
}

export default postsReducer;
